import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import './ScanningPage.css';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import RiskReportInfoWrapper from '../components/RiskReportInfoWrapper';
import BackgroundWrapper2 from '../components/BackgroundWrapper2';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ScanningPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { framework, file, fileExt, fileName } = location.state || {};

  const [error, setError] = useState<string>('');
  const hasStartedRef = useRef(false);

  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // Start scan on mount - using SSE via Lambda Function URL
  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    if (!framework || !file) {
      setError('Missing framework or file information');
      return;
    }
    
    const startScan = async () => {
      try {
        const formData = new FormData();
        formData.append('framework', framework);
        formData.append('file', file);

        // Use streaming response (Lambda Function URL, no /api prefix)
        const response = await fetch(`${API_BASE_URL}/agentic-workflow/scan-file`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        // Read entire response as text (waits until stream is fully done)
        const text = await response.text();

        // Find the "scan_data: " line (the final result, ignoring heartbeats)
        const lines = text.split('\n');
        const dataLine = lines.reverse().find(l => l.startsWith('scan_data: '));

        if (!dataLine) {
          throw new Error('No result received');
        }

        const data = JSON.parse(dataLine.replace(/^scan_data: /, ''));

        if (data.status === 'done') {
          navigate('/results', {
            state: {
              graphData: {
                nodes: data.result.nodes || [],
                edges: data.result.edges || [],
                agents: data.result.agents || [],
                tools: data.result.tools || [],
                mcp_servers: data.result.mcp_servers || [],
                name: data.result.name,
                modified_workflow: data.result.modified_workflow,
              },
              fileExt,
              fileName: fileName,
            },
          });
        } else if (data.status === 'error') {
          throw new Error(data.detail);
        }
      } catch (e: any) {
        setError('Scan has failed. Please check that the file and the framework match.');
        console.error('Scan error:', e);
      }
    };

    startScan();
  }, [framework, file, fileExt, fileName, navigate]);

  return (
    <BackgroundWrapper2>
      <div className="scanning-container">
        <Header />
        <main className="scanning-main">

          {/* Left side */}
          <RiskReportInfoWrapper/>

          {/* Right side */}
          <div className="scanning-right-card">
            <h1 className="scanning-title">Scanning in Progress...</h1>
            <div className="scanning-stopwatch">
              ⏱️ {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}
            </div>
            <p className="scanning-subtitle">
              Analyzing your agent workflow for security vulnerabilities
            </p>
            {error ? (
              <>
                <p className="scanning-error-text">{error}</p>
                <button onClick={() => navigate('/upload')} className="scanning-go-back-button ">
                  Go Back
                </button>
              </>
            ) : (
              <p className="scanning-wait-text">
                Please wait while we analyze your file, 
                <br></br>
                this may take a few minutes
              </p>
            )}
          </div>
        </main>
      </div>
    </BackgroundWrapper2>
  );
};

export default ScanningPage;